import VisitAPI from '../serviceAPI/VisitAPI';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DecryptData, EncryptData } from '../util/DataEncryption';

const initialState = {
  visitData: [],
  visitDataLast12Months: [], // added this to initial state
  selectedVisitId: sessionStorage.getItem('selectedVisitId')
    ? DecryptData(sessionStorage.getItem('selectedVisitId'))
    : null,
  selectedVisitName: sessionStorage.getItem('selectedVisitName')
    ? DecryptData(sessionStorage.getItem('selectedVisitName'))
    : null,
  selectedVisitEndDate: sessionStorage.getItem('selectedVisitEndDate')
    ? DecryptData(sessionStorage.getItem('selectedVisitEndDate'))
    : null,
  isLoading: false,
  selectedVisitIsCompleted: sessionStorage.getItem('selectedVisitIsCompleted')
    ? DecryptData(sessionStorage.getItem('selectedVisitIsCompleted'))
    : false,
  prevThumbnailDetails: null,
  prevThumbnailId: '',
  prevPhotoFileId: "",
  prevPhotoFileDetails: "",
  isInternalVisit: sessionStorage.getItem('isInternalVisit')
    ? DecryptData(sessionStorage.getItem('isInternalVisit'))
    : false,
  errorCode: null,
  updatedVisitData: {}
};

export const getAllVisits = createAsyncThunk(
  'visits/getAllVisits',
  async (officeId, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.GetAllVisits(officeId);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

export const getVisitsLast12Months = createAsyncThunk(
  'visits/getVisitsLast12Months',
  async (officeId, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.GetAllVisits12Months(officeId);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message || 'Network Error');
    }
  }
);

export const createNewVisit = createAsyncThunk(
  'visits/createNewVisit',
  async (newData, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.CreateNewVisit(newData);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

export const updateVisit = createAsyncThunk(
  'visits/updateVisit',
  async ({ newData, id }, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.UpdateVisit(newData, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

export const updateVisitSpeakers = createAsyncThunk(
  'visits/updateVisitSpeakers',
  async ({ speakers, id }, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.UpdateVisitSpeakers(speakers, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

export const updateVisitAttendees = createAsyncThunk(
  'visits/updateVisitAttendees',
  async ({ attendees, id }, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.UpdateVisitAttendees(attendees, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

export const updateVisitResources = createAsyncThunk(
  'visits/updateVisitResources',
  async ({ resources, id }, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.UpdateVisitResources(resources, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

export const deleteVisit = createAsyncThunk(
  'visits/deleteVisit',
  async (id, { rejectWithValue }) => {
    try {
      const res = await VisitAPI.DeleteVisit(id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || 'Network Error');
    }
  }
);

const VisitSlice = createSlice({
  name: 'visits',
  initialState,
  reducers: {
    updateVisitId: (state, action) => {
      sessionStorage.setItem('selectedVisitId', EncryptData(action.payload.id));
      sessionStorage.setItem('selectedVisitName', EncryptData(action.payload.name));
      sessionStorage.setItem('selectedVisitEndDate', EncryptData(action.payload.endDate));
      sessionStorage.setItem('isInternalVisit', EncryptData(action.payload.isInternalVisit));

      state.selectedVisitId = action.payload.id;
      state.selectedVisitName = action.payload.name;
      state.selectedVisitEndDate = action.payload.endDate;
      state.isInternalVisit = action.payload.isInternalVisit;
    },
    updatedIsVisitCompleted: (state, action) => {
      sessionStorage.setItem('selectedVisitIsCompleted', EncryptData(action.payload));
      state.selectedVisitIsCompleted = action.payload;
    },
    setPrevThumbnailDetails: (state, action) => {
      state.prevThumbnailDetails = action.payload;
    },
    setPreThumbnailId: (state, action) => {
      state.prevThumbnailId = action.payload;
    },
    setPrevPhotoFileDetails: (state, action) => {
      state.prevPhotoFileDetails = action.payload;
    },
    setPrePhotoFileId: (state, action) => {
      state.prevPhotoFileId = action.payload;
    },
    resetErrorAlert: (state) => {
      state.errorCode = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllVisits.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllVisits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.visitData = [...action.payload];
      })
      .addCase(getAllVisits.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload;
      })
      .addCase(getVisitsLast12Months.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getVisitsLast12Months.fulfilled, (state, action) => {
        state.isLoading = false;
        state.visitDataLast12Months = action.payload;
      })
      .addCase(getVisitsLast12Months.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload;
      })
    .addCase(createNewVisit.fulfilled, (state, action) => {
      state.visitData.push(action.payload);
    })
    .addCase(createNewVisit.rejected, (state, action) => {
      state.errorCode = action.payload;
    })
    .addCase(updateVisit.fulfilled, (state, action) => {
      state.visitData = state.visitData.map((visit) =>
        visit._id === action.payload._id ? action.payload : visit
      );
    })
    .addCase(updateVisit.rejected, (state, action) => {
      state.errorCode = action.payload;
    })
    .addCase(updateVisitSpeakers.fulfilled, (state, action) => {
      state.updatedVisitData = action.payload;
    })
    .addCase(updateVisitSpeakers.rejected, (state, action) => {
      state.errorCode = action.payload;
    })
    .addCase(updateVisitAttendees.fulfilled, (state, action) => {
      state.updatedVisitData = action.payload;
    })
    .addCase(updateVisitAttendees.rejected, (state, action) => {
      state.errorCode = action.payload;
    })
    .addCase(updateVisitResources.fulfilled, (state, action) => {
      state.updatedVisitData = action.payload;
    })
    .addCase(updateVisitResources.rejected, (state, action) => {
      state.errorCode = action.payload;
    })
    .addCase(deleteVisit.fulfilled, (state, action) => {
      const id = action.payload._id;
      const index = state.visitData.findIndex((item) => item._id === id);
      if (index !== -1) {
        state.visitData.splice(index, 1);
      }
    })
    .addCase(deleteVisit.rejected, (state, action) => {
      state.errorCode = action.payload;
    });
}
});

export const {
  setPrevThumbnailDetails,
  setPreThumbnailId,
  setPrePhotoFileId,
  setPrevPhotoFileDetails,
  updateVisitId,
  updatedIsVisitCompleted,
  resetErrorAlert
} = VisitSlice.actions;

export default VisitSlice.reducer;